import { calculateItemMetrics, COMMON_METRIC_ORDER, uniqueValues } from '@/lib/metrics'
import { TransposedDataGrid } from './TransposedDataGrid'
import type { MetricsDataItem } from '@/entities/dailyMetric'

interface CommonMetricsTableProps {
  metricsData: MetricsDataItem[]
}

export function CommonMetricsTable({ metricsData }: CommonMetricsTableProps) {
  // Union generalMetrics and techMetrics into one mapping.
  const colsData = metricsData.map((item) => ({
    date: item.date,
    metrics: calculateItemMetrics(item, { raw: true }) as Record<string, number>,
  }))

  const metricOrder = COMMON_METRIC_ORDER
  const uniqueMetricTypes = uniqueValues(colsData.flatMap((item) => Object.keys(item.metrics || {})))

  // Filter out error and error% metrics
  const metricTypes = metricOrder
    .filter((metric) => uniqueMetricTypes.includes(metric))
    .filter((metric) => !metric.toLowerCase().includes('error'))

  // Helper to get all raw values for a specific metric across all dates
  const getAllValues = (metricType: string) => {
    return colsData.map((item) => item.metrics[metricType] || 0)
  }

  // Create rows for each metric type
  const rows = metricTypes.map((metricType, index) => {
    const row: Record<string, string | number> = {
      id: index,
      metric: metricType,
    }
    const isFloatMetric = colsData.some((item) => !Number.isInteger(item.metrics[metricType] || 0))
    const values = colsData.map((item) => {
      const value = (item.metrics[metricType] as number) || 0
      if (metricType.includes('%')) {
        row[item.date] = value > 0 ? `${value.toFixed(2)}%` : ''
      } else if (isFloatMetric) {
        row[item.date] = value > 0 ? value.toFixed(1) : ''
      } else {
        row[item.date] = value > 0 ? value : ''
      }
      return value
    })

    // Calculate total based on metric type
    if (metricType.includes('%')) {
      row['total'] = '-'
    } else if (metricType === 'Average handling time') {
      const totalMinutes = getAllValues('Total minutes').reduce((acc, val) => acc + val, 0)
      const connectedToAI = getAllValues('Connected to AI').reduce((acc, val) => acc + val, 0)
      const value = connectedToAI > 0 ? (totalMinutes * 60) / connectedToAI : 0
      row['total'] = value > 0 ? value.toFixed(1) : ''
    } else if (metricType === '% Connected to AI') {
      const totalConnectedToAI = getAllValues('Connected to AI').reduce((acc, val) => acc + val, 0)
      const totalCalls = getAllValues('Total calls').reduce((acc, val) => acc + val, 0)
      const percentage = totalCalls > 0 ? (totalConnectedToAI / totalCalls) * 100 : 0
      row['total'] = percentage > 0 ? `${percentage.toFixed(2)}%` : ''
    } else if (metricType === '% Connected to customer') {
      const totalConnectedToCustomer = getAllValues('Connected to customer').reduce((acc, val) => acc + val, 0)
      const totalConnectedToAI = getAllValues('Connected to AI').reduce((acc, val) => acc + val, 0)
      const percentage = totalConnectedToAI > 0 ? (totalConnectedToCustomer / totalConnectedToAI) * 100 : 0
      row['total'] = percentage > 0 ? `${percentage.toFixed(2)}%` : ''
    } else if (metricType === '% Successful calls') {
      const totalSuccessful = getAllValues('Successful calls').reduce((acc, val) => acc + val, 0)
      const totalConnectedToCustomer = getAllValues('Connected to customer').reduce((acc, val) => acc + val, 0)
      const percentage = totalConnectedToCustomer > 0 ? (totalSuccessful / totalConnectedToCustomer) * 100 : 0
      row['total'] = percentage > 0 ? `${percentage.toFixed(2)}%` : ''
    } else if (metricType === 'Cost per success') {
      const totalCost = getAllValues('Cost ($)').reduce((acc, val) => acc + val, 0)
      const totalSuccessfulCalls = getAllValues('Successful calls').reduce((acc, val) => acc + val, 0)
      const value = totalSuccessfulCalls > 0 ? totalCost / totalSuccessfulCalls : 0
      row['total'] = value > 0 ? value.toFixed(2) : ''
    } else {
      const sum = values.reduce((acc, value) => acc + value, 0)
      if (sum > 0) {
        row['total'] = isFloatMetric ? sum.toFixed(2) : sum
      } else {
        row['total'] = ''
      }
    }

    return row
  })

  return (
    <div className="bg-white rounded-lg shadow-sm border border overflow-hidden">
      <TransposedDataGrid
        rows={rows}
        firstColumn={{
          field: 'metric',
          headerName: 'Metric',
        }}
        lastColumn={{
          field: 'total',
          headerName: 'Total',
          type: 'number' as const,
        }}
        dates={Array.from(new Set(metricsData.map((metric) => metric.date)))}
      />
    </div>
  )
}